import React, { useState, useEffect } from 'react';
import { Menu, X, User, ShoppingCart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import FullScreenMenu from './FullScreenMenu';
import MusicPlayer from './MusicPlayer';
import { useCart } from '../context/CartContext';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCartPreview, setShowCartPreview] = useState(false);
  const location = useLocation();
  const { getCartCount, cart, removeFromCart, getTotalPrice } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Prevent scrolling when menu is open
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to always restore scroll on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Additional safeguard: restore scroll on component mount
  useEffect(() => {
    document.body.style.overflow = 'unset';
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-black/80 backdrop-blur-lg' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 py-2">
          <div className="relative w-full max-w-[2000px] mx-auto">
            {/* Main horizontal row - all items aligned at same height */}
            <div className="flex items-center justify-between w-full">
              {/* Left Side: Logo with Get in Touch horizontally next to it */}
              <div className="flex items-center z-50 gap-4">
                {/* Logo */}
                <Link to="/" className="flex items-center">
                  <img
                    src="/artourius-logo.png"
                    alt="Artourius Design"
                    className="h-32 sm:h-40 w-auto transition-all duration-500 hover:scale-105"
                  />
                </Link>
                
                {/* Get in Touch Button - horizontally aligned with logo */}
                <Link
                  to="/contact"
                  className="hidden lg:block px-4 py-2 border-2 border-[hsl(45,100%,70%)] text-[hsl(45,100%,70%)] rounded-full font-medium text-sm tracking-wide hover:bg-[hsl(45,100%,70%)] hover:text-black transition-all duration-300 whitespace-nowrap"
                >
                  Get in touch
                </Link>
              </div>

              {/* Center: Music Player - Always mounted but only visible on Landing Page */}
              <div className={`absolute left-1/2 transform -translate-x-1/2 ${location.pathname === '/' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <MusicPlayer />
              </div>

              {/* Right Side: Login, Cart and Menu - Aligned with Logo center */}
              <div className="flex items-center gap-4 z-50">
              {/* Login Link (Desktop) */}
              <Link
                to="/login"
                className="hidden lg:flex items-center gap-2 px-4 py-2 text-white hover:text-[hsl(45,100%,70%)] transition-colors font-medium text-base"
              >
                <User className="w-6 h-6" />
                <span>Login</span>
              </Link>

              {/* Cart Icon with Hover Dropdown - Bigger */}
              <div 
                className="relative"
                onMouseEnter={() => setShowCartPreview(true)}
                onMouseLeave={() => setShowCartPreview(false)}
              >
                <Link
                  to="/cart"
                  className="relative px-3 py-3 text-white hover:text-[hsl(45,100%,70%)] transition-colors block"
                >
                  <ShoppingCart className="w-8 h-8" />
                  {getCartCount() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[hsl(45,100%,70%)] text-black text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center">
                      {getCartCount()}
                    </span>
                  )}
                </Link>

                {/* Cart Preview Dropdown */}
                {showCartPreview && getCartCount() > 0 && (
                  <div className="absolute right-0 top-full mt-0 w-80 bg-black/95 backdrop-blur-lg border border-[hsl(45,100%,70%)] rounded-2xl shadow-2xl p-6 z-50">
                    <h3 className="text-white font-bold text-lg mb-4 flex items-center justify-between">
                      <span>My Cart</span>
                      <span className="text-[hsl(45,100%,70%)] text-sm">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
                    </h3>

                    {/* Cart Items */}
                    <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                      {cart.map((item, index) => (
                        <div key={index} className="flex gap-3 items-start p-2 bg-gray-900/50 rounded-lg">
                          <img 
                            src={item.image || item.images?.[0] || '/placeholder.png'} 
                            alt={item.name || 'Product'}
                            className="w-16 h-16 object-cover rounded"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/placeholder.png';
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white text-sm font-medium truncate">{item.name}</h4>
                            <p className="text-gray-400 text-xs">{item.category}</p>
                            {item.selectedSize && (
                              <p className="text-gray-500 text-xs">Size: {item.selectedSize}</p>
                            )}
                            <p className="text-[hsl(45,100%,70%)] text-sm font-bold mt-1">{item.price}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Total */}
                    <div className="border-t border-gray-800 pt-4 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400">Subtotal</span>
                        <span className="text-white font-bold text-lg">{getTotalPrice()}</span>
                      </div>
                    </div>

                    {/* Checkout Button */}
                    <Link
                      to="/cart"
                      className="w-full py-3 bg-[hsl(45,100%,70%)] text-black font-bold rounded-full text-center hover:bg-[hsl(45,100%,60%)] transition-all duration-300 block"
                    >
                      View Cart & Checkout
                    </Link>
                  </div>
                )}
              </div>

              {/* Menu Button - Positioned Right (Desktop) - Bigger */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="hidden lg:flex relative w-16 h-16 rounded-full bg-[hsl(45,100%,70%)] items-center justify-center transition-all duration-300 hover:scale-110 group overflow-hidden z-50"
              >
                <div className="relative z-10">
                  {isMenuOpen ? (
                    <X className="w-8 h-8 text-black transition-transform duration-300 rotate-90" />
                  ) : (
                    <Menu className="w-8 h-8 text-black transition-transform duration-300" />
                  )}
                </div>
                {/* Ripple effect on hover */}
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
              </button>

              {/* Mobile Menu Button - Bigger */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden w-14 h-14 rounded-full bg-[hsl(45,100%,70%)] flex items-center justify-center transition-transform duration-300 hover:scale-110 z-50"
              >
                {isMenuOpen ? (
                  <X className="w-7 h-7 text-black transition-transform duration-300 rotate-90" />
                ) : (
                  <Menu className="w-7 h-7 text-black transition-transform duration-300" />
                )}
              </button>
            </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Full Screen Menu */}
      <FullScreenMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </>
  );
};

export default Navigation;